import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import MetadataStepShell from '@/components/step-shells/MetadataStepShell';
import { useCourse } from '@/contexts/CourseContext';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const REQUIRED_METADATA_FIELDS = [
    'title',
    'description',
    'totalHours',
    'educationalLevel',
    'grade',
    'subjectArea'
];

// REMOVED: METADATA_FIELD_LABELS (Greek) and PROCEED_TO_GOALS_REGEX
// Transitions are now handled entirely by backend for proper validation and language support

const getMissingMetadataFields = (metadata = {}) => {
    const missing = [];

    REQUIRED_METADATA_FIELDS.forEach((field) => {
        const value = metadata[field];

        if (field === 'totalHours') {
            if (value === undefined || value === null || value === '' || Number(value) <= 0) {
                missing.push(field);
            }
        } else if (typeof value === 'string') {
            if (!value.trim()) {
                missing.push(field);
            }
        } else if (value === undefined || value === null) {
            missing.push(field);
        }
    });

    return missing;
};

/**
 * ImportStep1Metadata
 * Metadata validation phase where the MetadataValidationAgent:
 * 1. Shows initial analysis of imported metadata
 * 2. Chats with user to fill in missing/invalid metadata
 * 3. Asks for final confirmation before locking metadata
 * 4. Transitions to goals validation (step 2)
 */
export default function ImportStep1Metadata({ courseId, importSessionId, initialCourseData, onComplete, setCustomChatHandler, updateCourseData }) {
    const { dispatch, course } = useCourse();
    const [metadata, setMetadata] = useState(null);
    const [showPreview, setShowPreview] = useState(false);
    const [stepInitialized, setStepInitialized] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);

    // CRITICAL: Set import mode when component mounts
    useEffect(() => {
        if (importSessionId) {
            dispatch({
                type: 'SET_IMPORT_MODE',
                payload: { sessionId: importSessionId, executionPlan: null }
            });
        }
    }, [importSessionId, dispatch]);

    // Initialize Step 1 when component mounts
    useEffect(() => {
        if (!stepInitialized && course && course.id) {
            setStepInitialized(true);

            // Detect language for the hidden prompt
            const courseLanguage = course?.language || 'English';
            const isGreek = courseLanguage.toLowerCase() === 'greek' ||
                courseLanguage.toLowerCase() === 'ελληνικά' ||
                /[\u0370-\u03FF]/.test(course?.title || '');

            // Triger backend analysis
            const initPrompt = isGreek
                ? "Μόλις ανέβασα το αρχείο μαθήματος. Παρακαλώ ανάλυσε τα μεταδεδομένα (τίτλος, περιγραφή, κτλ) και πες μου τι λείπει ή τι μπορούμε να βελτιώσουμε."
                : "I just uploaded the course file. Please analyze the metadata (title, description, etc) and tell me what is missing or can be improved.";

            // We don't show the user prompt in the UI to make it feel like the agent speaks first
            // But we do send it to the backend to trigger the response
            import('@/services/api').then(({ chatAPI }) => {
                chatAPI.sendMessage(course.id, initPrompt, 1)
                    .then(response => {
                        if (response && response.reply) {
                            dispatch({
                                type: 'ADD_CHAT_MESSAGE',
                                payload: {
                                    role: 'assistant',
                                    text: response.reply
                                }
                            });
                        }
                    })
                    .catch(err => console.error("Failed to trigger initial analysis:", err));
            });
        }
    }, [stepInitialized, course, dispatch]);

    // Process course data when it becomes available
    useEffect(() => {
        if (!initialCourseData || Object.keys(initialCourseData).length === 0) return;
        if (metadata !== null) return; // Already set, don't overwrite

        const extractedMetadata = {
            title: initialCourseData.title || '',
            description: initialCourseData.description || '',
            totalHours: initialCourseData.totalHours || 0,
            educationalLevel: initialCourseData.educationalLevel || '',
            grade: initialCourseData.grade || '',
            subjectArea: initialCourseData.subjectArea || ''
        };

        console.log('ImportStep1Metadata: Setting metadata from initialCourseData:', extractedMetadata);
        setMetadata(extractedMetadata);
        setShowPreview(true);

        // Show loading indicator immediately, then get dynamic welcome from agent
        dispatch({
            type: 'ADD_CHAT_MESSAGE', payload: {
                role: 'assistant',
                isLoading: true
            }
        });

        // Get dynamic welcome from agent (context-aware based on missing fields)
        const getAgentWelcome = async () => {
            try {
                const id = courseId || importSessionId;
                const missingFields = getMissingMetadataFields(extractedMetadata);
                const message = missingFields.length === 0
                    ? "Hello! I'm ready to review the complete metadata. What should we focus on?"
                    : `Hello! I'm ready to review the metadata. I see some fields might be missing. What should we focus on?`;

                const response = await axios.post(`${API_URL}/chat/step1`, {
                    courseId: id,
                    message: message
                });

                // Remove loading indicator and add actual response
                dispatch({ type: 'REMOVE_LAST_MESSAGE' });
                dispatch({
                    type: 'ADD_CHAT_MESSAGE', payload: {
                        role: 'assistant',
                        text: response.data.response || 'Γεια σας! Ας ελέγξουμε τα μεταδεδομένα του μαθήματός σας.'
                    }
                });
            } catch (error) {
                console.error('Error getting agent welcome:', error);
                // Remove loading indicator and add fallback message
                dispatch({ type: 'REMOVE_LAST_MESSAGE' });

                const missingFields = getMissingMetadataFields(extractedMetadata);
                const fallbackText = missingFields.length === 0
                    ? 'Τα μεταδεδομένα του μαθήματος είναι πλήρη! Μπορείτε να τα επεξεργαστείτε αν θέλετε.'
                    : `Γεια σας! Ας ελέγξουμε τα μεταδεδομένα του μαθήματός σας.`;

                dispatch({
                    type: 'ADD_CHAT_MESSAGE', payload: {
                        role: 'assistant',
                        text: fallbackText
                    }
                });
            }
        };

        getAgentWelcome();
    }, [initialCourseData, metadata, dispatch, courseId, importSessionId]);

    // REMOVED: handleForceProceed - transitions now handled entirely by backend
    // This ensures proper validation, language-aware responses, and metadataLocked flag is set

    const handleSendMessage = useCallback(async (userMessage) => {
        if (!userMessage.trim() || isProcessing) return;

        setIsProcessing(true);
        dispatch({ type: 'SET_LOADING', payload: true });

        // Remove any existing loading indicators first to prevent duplicates
        dispatch({ type: 'REMOVE_LOADING_MESSAGES' });

        // Add user message to chat
        dispatch({ type: 'ADD_CHAT_MESSAGE', payload: { role: 'user', text: userMessage } });

        // Add typing indicator
        dispatch({ type: 'ADD_CHAT_MESSAGE', payload: { role: 'assistant', isLoading: true } });

        try {
            // REMOVED: Frontend bypass for "proceed" messages
            // Let the backend handle all transition requests to ensure proper validation
            // and language-aware responses. The backend will:
            // 1. Validate all required fields
            // 2. Set metadataLocked=True on success
            // 3. Return step_complete=True only when validated

            // Use unified chat endpoint for Step 1 (works for both build and import)
            // Use courseId prop if available, fall back to importSessionId for backward compatibility
            const id = courseId || importSessionId;
            const response = await axios.post(`${API_URL}/chat/step1`, {
                courseId: id,
                message: userMessage
            });

            // Remove typing indicator
            dispatch({ type: 'REMOVE_LAST_MESSAGE' });

            // Add assistant response
            const assistantText = response.data.response || response.data.message || ' ';
            dispatch({
                type: 'ADD_CHAT_MESSAGE', payload: {
                    role: 'assistant',
                    text: assistantText
                }
            });

            // Update metadata from backend response (course_data contains the updated course)
            if (response.data.course_data) {
                const courseData = response.data.course_data;
                const extractedMetadata = {
                    title: courseData.title || '',
                    description: courseData.description || '',
                    totalHours: courseData.totalHours || 0,
                    educationalLevel: courseData.educationalLevel || '',
                    grade: courseData.grade || '',
                    subjectArea: courseData.subjectArea || ''
                };
                setMetadata(extractedMetadata);
                setShowPreview(true);

                // CRITICAL: Update CourseContext so ProgressBar's "Continue" button enables
                dispatch({ type: 'SET_COURSE', payload: courseData });
            }

            // Check if agent wants to proceed to next step
            if (response.data.step_complete || response.data.next_step === 2) {
                if (onComplete) {
                    onComplete({
                        metadata: metadata
                    });
                }
                return;
            }

        } catch (error) {
            console.error('Error sending message:', error);
            dispatch({ type: 'REMOVE_LAST_MESSAGE' }); // Remove typing indicator
            dispatch({
                type: 'ADD_CHAT_MESSAGE', payload: {
                    role: 'assistant',
                    text: '❌ Συγγνώμη, παρουσιάστηκε σφάλμα. Παρακαλώ δοκιμάστε ξανά.'
                }
            });
        } finally {
            setIsProcessing(false);
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    }, [courseId, importSessionId, isProcessing, onComplete, metadata, dispatch]);

    // Register custom chat handler with parent using Ref pattern to avoid infinite loops
    const handlerRef = React.useRef(handleSendMessage);
    useEffect(() => {
        handlerRef.current = handleSendMessage;
    });

    useEffect(() => {
        if (setCustomChatHandler) {
            setCustomChatHandler(() => (msg) => handlerRef.current(msg));
        }
        return () => {
            if (setCustomChatHandler) {
                setCustomChatHandler(null);
            }
        };
    }, [setCustomChatHandler]);

    const handleEdit = async (field, value) => {
        if (!updateCourseData) return;
        const updates = { [field]: value };
        await updateCourseData(updates);
        // Update local metadata state
        setMetadata(prev => ({ ...prev, [field]: value }));
    };

    return (
        <MetadataStepShell
            showPreview={showPreview}
            metadata={metadata || {}}
            headerTitle="Έλεγχος & Συμπλήρωση Μεταδεδομένων"
            headerSubtitle={showPreview
                ? 'Συνεργαστείτε με τον Coby για να βελτιώσετε τα μεταδεδομένα του μαθήματός σας'
                : 'Γεια σας! Ας ελέγξουμε τα μεταδεδομένα του μαθήματός σας'}
            onEdit={handleEdit}
        />
    );
}
